import { AnimatedSkeletonList } from "@/components/common/AnimatedSkeletonList/AnimatedSkeletonList"
import { Modal } from "@/components/common/Modal/Modal"
import { toast } from "@/components/common/Toast"
import { KnowledgebaseFileTable } from "@/components/feature/KnowledgebaseFileTable"
import {
  DEFAULT_FILTERS,
  KnowledgebaseFilterBar,
  type KnowledgebaseFilters,
} from "@/components/feature/KnowledgebaseFilters"
import { FilePreviewModal } from "@/components/feature/Modals/FilePreviewModal/FilePreviewModal"
import { BulkTagDocumentDialog } from "@/components/feature/Modals/TagDocumentDialog/BulkTagDocumentDialog"
import { TagDocumentDialog } from "@/components/feature/Modals/TagDocumentDialog/TagDocumentDialog"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useRegisterAgentContext } from "@/features/agent-chat/context/AgentPageContext"
import { useFilteredKnowledgebaseFiles } from "@/hooks/useFilteredKnowledgebaseFiles"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useDeleteKnowledgebaseFiles } from "@/services/api/knowledgebaseService"
import type { KnowledgebaseFile } from "@/types/knowledgebase"
import type { KnowledgeBaseCategory } from "@/types/knowledgebaseCategories"
import { classNames } from "@/utils/classNames"
import { IconLoader2, IconTrash, type IconProps } from "@tabler/icons-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router"

interface KnowledgebaseFileListSectionProps {
  displayFiles: KnowledgebaseFile[]
  isFilesPending: boolean
  refetchFiles: () => void
  filterCategories: KnowledgeBaseCategory[]
  isDatabaseTourActive: boolean
  isDatabaseFilesStepActive: boolean
}

const SpinnerActionIcon: React.FC<IconProps> = ({ size }) => (
  <IconLoader2 size={size ?? 16} className="animate-spin" />
)

/** Cap on files named in agent page context. Only bites on the unnarrowed
 * list; the complete library stays reachable via list_kb_documents. */
const AGENT_CONTEXT_FILE_LIMIT = 25

export const KnowledgebaseFileListSection: React.FC<
  KnowledgebaseFileListSectionProps
> = ({
  displayFiles,
  isFilesPending,
  refetchFiles,
  filterCategories,
  isDatabaseTourActive,
  isDatabaseFilesStepActive,
}) => {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const isFolderFlowEnabled = useFeatureFlag("KB_TAGGING_FLOW")

  const [filters, setFilters] = useState<KnowledgebaseFilters>(DEFAULT_FILTERS)
  const [showAllTags, setShowAllTags] = useState(true)
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set())
  const [isBulkTagModalOpen, setIsBulkTagModalOpen] = useState(false)
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false)
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false)
  const [tagFile, setTagFile] = useState<KnowledgebaseFile | null>(null)

  const bulkDeleteInFlightRef = useRef(false)
  const { mutate: deleteFiles, isPending: isBulkDeletePending } =
    useDeleteKnowledgebaseFiles()

  const { filteredAndSortedFiles, selectableFiles } =
    useFilteredKnowledgebaseFiles({
      files: displayFiles,
      filters,
      enableDocumentTagFiltering: isFolderFlowEnabled && !isDatabaseTourActive,
    })

  const isSelectionMode = selectedFileIds.size > 0

  const toggleFileSelection = useCallback((id: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    setSelectedFileIds(new Set(selectableFiles.map((f) => f.id)))
  }, [selectableFiles])

  const deselectAll = useCallback(() => {
    setSelectedFileIds(new Set())
  }, [])

  useEffect(() => {
    setSelectedFileIds(new Set())
  }, [filters])

  // Bulk tag
  const handleBulkTag = () => {
    setIsBulkTagModalOpen(true)
  }

  const bulkTagFileIds = useMemo(
    () => Array.from(selectedFileIds),
    [selectedFileIds],
  )

  const bulkTagFileNames = useMemo(
    () =>
      bulkTagFileIds
        .map((id) => displayFiles.find((f) => f.id === id)?.fileName ?? id)
        .slice(0, 5),
    [bulkTagFileIds, displayFiles],
  )

  // Bulk delete
  const handleBulkDelete = () => {
    setIsBulkDeleteModalOpen(true)
  }

  const confirmBulkDelete = () => {
    if (bulkDeleteInFlightRef.current || isBulkDeletePending) {
      return
    }

    const ids = Array.from(selectedFileIds)
    if (ids.length === 0) {
      return
    }

    bulkDeleteInFlightRef.current = true

    deleteFiles(ids, {
      onSuccess: (data) => {
        bulkDeleteInFlightRef.current = false
        const failedCount = Object.keys(data.failed).length
        if (failedCount > 0 && data.success.length > 0) {
          toast.warning(translate("KNOWLEDGEBASE/BULK_DELETE_PARTIAL_ERROR"))
        } else if (failedCount > 0) {
          toast.error(translate("KNOWLEDGEBASE/BULK_DELETE_ERROR"))
        } else {
          toast.success(translate("KNOWLEDGEBASE/BULK_DELETE_SUCCESS"))
        }
        setSelectedFileIds(new Set())
        setIsBulkDeleteModalOpen(false)
        refetchFiles()
      },
      onError: () => {
        bulkDeleteInFlightRef.current = false
        toast.error(translate("KNOWLEDGEBASE/BULK_DELETE_ERROR"))
        setIsBulkDeleteModalOpen(false)
      },
    })
  }

  // URL-driven preview modal
  const location = useLocation()
  const urlQueryId = useMemo(
    () => new URLSearchParams(location.search).get("file"),
    [location.search],
  )

  useEffect(() => {
    if (urlQueryId) {
      setIsPreviewModalOpen(true)
    }
  }, [urlQueryId])

  // What the user means by "this file" / "these two", most specific first:
  // an explicit selection, else the file open in preview, else whatever the
  // current filters leave on screen. Tour demo files are excluded — their ids
  // resolve to nothing server-side.
  const agentDocuments = useMemo(() => {
    if (isDatabaseTourActive) return []
    if (selectedFileIds.size > 0) {
      return filteredAndSortedFiles.filter((file) =>
        selectedFileIds.has(file.id),
      )
    }
    const previewed =
      isPreviewModalOpen && urlQueryId
        ? filteredAndSortedFiles.find((file) => file.id === urlQueryId)
        : undefined
    return previewed ? [previewed] : filteredAndSortedFiles
  }, [
    isDatabaseTourActive,
    selectedFileIds,
    filteredAndSortedFiles,
    isPreviewModalOpen,
    urlQueryId,
  ])

  useRegisterAgentContext({
    route: location.pathname,
    entities: {
      visible_documents: agentDocuments
        .slice(0, AGENT_CONTEXT_FILE_LIMIT)
        .map((file) => ({ document_id: file.id, file_name: file.fileName })),
    },
  })

  // Open tag dialog from the file list (row tag button)
  const handleOpenTagDialog = useCallback((file: KnowledgebaseFile) => {
    setTagFile(file)
  }, [])

  // "View Details" clicked inside FilePreviewModal → open tag dialog for same file
  const handleViewDetailsFromPreview = useCallback(() => {
    if (!urlQueryId) return
    const file = displayFiles.find((f) => f.id === urlQueryId)
    if (file) setTagFile(file)
  }, [urlQueryId, displayFiles])

  // "View" clicked inside TagDocumentDialog → open preview for that file
  const handleViewFileFromTag = useCallback(() => {
    if (!tagFile) return
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set("file", tagFile.id)
    navigate(`${window.location.pathname}?${searchParams.toString()}`, {
      replace: true,
    })
    setIsPreviewModalOpen(true)
  }, [tagFile, navigate])

  const handleTagDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setTagFile(null)
        refetchFiles()
      }
    },
    [refetchFiles],
  )

  return (
    <>
      {isPreviewModalOpen && (
        <FilePreviewModal
          urlQueryId={urlQueryId || undefined}
          setIsPreviewModalOpen={setIsPreviewModalOpen}
          sidebarOpen={!!tagFile}
          onViewDetails={tagFile ? undefined : handleViewDetailsFromPreview}
        />
      )}
      {tagFile && (
        <TagDocumentDialog
          open={!!tagFile}
          onOpenChange={handleTagDialogOpenChange}
          fileId={tagFile.id}
          fileName={tagFile.fileName}
          expiryDate={tagFile.expiryDate}
          createdAt={tagFile.createdAt}
          source={tagFile.source}
          fileSizeBytes={tagFile.fileSizeBytes}
          onViewFile={isPreviewModalOpen ? undefined : handleViewFileFromTag}
          preventCloseOnOutsideInteraction={isPreviewModalOpen}
        />
      )}
      {isBulkDeleteModalOpen && (
        <Modal
          showSidebarLeft
          ModalIcon={IconTrash}
          descriptionText={translate(
            "KNOWLEDGEBASE/BULK_DELETE_MODAL_DESCRIPTION",
            { count: selectedFileIds.size },
          )}
          primaryActionTitle={translate(
            "KNOWLEDGEBASE/FILE_MODAL_PRIMARY_ACTION",
          )}
          primaryActionIcon={
            isBulkDeletePending ? SpinnerActionIcon : undefined
          }
          primaryActionIconPosition="left"
          secondaryActionTitle={translate(
            "KNOWLEDGEBASE/FILE_MODAL_SECONDARY_ACTION",
          )}
          onClose={() => setIsBulkDeleteModalOpen(false)}
          onPrimaryAction={confirmBulkDelete}
          onSecondaryAction={() => setIsBulkDeleteModalOpen(false)}
          isPrimaryActionDisabled={isBulkDeletePending}
        />
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
        <div
          id="knowledgebase-file-list"
          data-agent-target="kb.file-list"
          className={classNames(
            "flex min-h-0 min-w-0 flex-1 flex-col",
            isDatabaseFilesStepActive ? "pointer-events-none opacity-60" : "",
          )}
          aria-disabled={isDatabaseFilesStepActive}
        >
          <>
            <div className="mb-6 shrink-0">
              <KnowledgebaseFilterBar
                filters={filters}
                onFiltersChange={setFilters}
                categories={filterCategories}
                showAllTags={showAllTags}
                onShowAllTagsChange={setShowAllTags}
                selectedCount={selectedFileIds.size}
                selectableCount={selectableFiles.length}
                onSelectAll={selectAll}
                onDeselectAll={deselectAll}
                onDeleteSelected={handleBulkDelete}
                onBulkTag={handleBulkTag}
              />
            </div>
          </>

          <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
            {isFilesPending && !isDatabaseTourActive ? (
              <AnimatedSkeletonList numSkeletons={5} />
            ) : (
              <KnowledgebaseFileTable
                files={filteredAndSortedFiles}
                availableCategories={showAllTags ? filterCategories : []}
                selectedFileIds={selectedFileIds}
                onToggleSelect={toggleFileSelection}
                isSelectionMode={isSelectionMode}
                selectableCount={selectableFiles.length}
                onSelectAll={selectAll}
                onDeselectAll={deselectAll}
                refetchFiles={refetchFiles}
                showAllTags={showAllTags}
                onOpenTagDialog={
                  isFolderFlowEnabled ? handleOpenTagDialog : undefined
                }
                activeFilterCategoryIds={
                  new Set(
                    Object.entries(filters.tags)
                      .filter(([, tagIds]) => tagIds.length > 0)
                      .map(([categoryId]) => categoryId),
                  )
                }
              />
            )}
          </div>
        </div>
      </div>

      {isBulkTagModalOpen && selectedFileIds.size > 0 && (
        <BulkTagDocumentDialog
          open={isBulkTagModalOpen}
          onOpenChange={(open) => {
            setIsBulkTagModalOpen(open)
            if (!open) {
              setSelectedFileIds(new Set())
              refetchFiles()
            }
          }}
          fileIds={bulkTagFileIds}
          fileNames={bulkTagFileNames}
        />
      )}
    </>
  )
}
